import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Public,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  registerables,
} from 'chart.js';
import { supabasePublic } from '../lib/supabase-public';
import { FinancialChart } from './charts/FinancialChart';
import { FinancialData } from './charts/types/chartTypes';
import { TransactionDetailTable, Transaction } from './TransactionDetailTable';
import { ExpensePieChart } from './charts/ExpensePieChart';

// Chart.js の全コンポーネント登録
ChartJS.register(...registerables);

interface PublicOrganizationData {
  organization: {
    id: string;
    name: string;
    address: string;
    unit_count: number;
    fiscal_year_start_month: number;
    created_at: string;
  };
  accountBalances: Array<{
    id: string;
    account_type: 'general' | 'reserve';
    balance: number;
    last_updated: string;
  }>;
  monthlyData: Array<{
    year: number;
    month: string;
    generalIncome: number;
    generalExpense: number;
    reserveIncome: number;
    reserveExpense: number;
  }>;
  yearlyData: Array<{
    year: string;
    generalIncome: number;
    generalExpense: number;
    reserveIncome: number;
    reserveExpense: number;
  }>;
  recentTransactions: Array<{
    id: string;
    transaction_date: string;
    amount: number;
    type: 'income' | 'expense';
    account_type: 'general' | 'reserve';
    category: string;
    description: string;
    reference_number?: string;
    year: number;
    month: number;
  }>;
  fiscalYearStartMonth: number;
}

const PublicDashboard: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicOrganizationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartPeriod, setChartPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [showGeneral, setShowGeneral] = useState(true);
  const [showReserve, setShowReserve] = useState(true);
  
  // クリックしたグラフの明細表示用state
  const [selectedChartData, setSelectedChartData] = useState<{
    period: string;
    accountType: 'general' | 'reserve';
    type: 'income' | 'expense';
    amount: number;
  } | null>(null);
  
  // 現在の会計年度
  const [currentFiscalYear, setCurrentFiscalYear] = useState<number>(new Date().getFullYear());
  
  // 円グラフで選択された摘要（棒グラフフィルタリング用）
  const [selectedDescription, setSelectedDescription] = useState<string | null>(null);

  // 公開データ取得
  useEffect(() => {
    const fetchPublicData = async () => {
      if (!token) {
        setError('アクセストークンが必要です');
        setLoading(false);
        return;
      }

      try {
        const { data: publicData, error: publicError } = await supabasePublic
          .rpc('get_public_organization_data', {
            access_token: token
          });

        if (publicError) {
          console.error('Public data fetch error:', publicError);
          setError(`データの取得に失敗しました: ${publicError.message || JSON.stringify(publicError)}`);
          setLoading(false);
          return;
        }

        if (!publicData || !publicData.organization) {
          setError('組織データが見つかりません');
          setLoading(false);
          return;
        }

        setData(publicData);
        
        // 会計年度に基づいて初期表示を設定
        const fiscalStart = publicData.fiscalYearStartMonth || 4;
        const now = new Date();
        const currentFiscalYear = now.getMonth() + 1 >= fiscalStart 
          ? now.getFullYear() 
          : now.getFullYear() - 1;
          
        setCurrentFiscalYear(currentFiscalYear);
        
      } catch (err: any) {
        console.error('Error fetching public data:', err);
        setError('データの取得中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicData();
  }, [token]);

  // 年度切り替え
  const handlePreviousYear = () => {
    setCurrentFiscalYear(prev => prev - 1);
  };

  const handleNextYear = () => {
    setCurrentFiscalYear(prev => prev + 1);
  };

  // 金額フォーマット
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount);
  };

  // 共通コンポーネント用のデータ変換
  const convertToFinancialData = (): FinancialData[] => {
    if (!data) return [];
    
    // 選択された摘要でフィルタリングする場合
    if (selectedDescription) {
      return convertFilteredFinancialData();
    }
    
    const isMonthly = chartPeriod === 'monthly';
    
    // 月別表示の場合は現在の会計年度のデータのみフィルタリング
    const filteredMonthlyData = isMonthly && data.monthlyData 
      ? data.monthlyData.filter(d => {
          const monthMatch = d.month.match(/(\d+)月/);
          if (monthMatch && d.year) {
            const monthNumber = parseInt(monthMatch[1]);
            const fiscalYear = monthNumber >= (data.fiscalYearStartMonth || 4) 
              ? d.year 
              : d.year - 1;
            return fiscalYear === currentFiscalYear;
          }
          return true;
        })
      : data.monthlyData;
    
    const sourceData = isMonthly ? filteredMonthlyData : data.yearlyData;
    
    return sourceData.map(d => ({
      year: isMonthly ? (d as any).year : parseInt((d as any).year),
      month: isMonthly ? (d as any).month : undefined,
      generalIncome: d.generalIncome,
      generalExpense: d.generalExpense,
      reserveIncome: d.reserveIncome,
      reserveExpense: d.reserveExpense,
    }));
  };
  
  // 選択された摘要でフィルタリングしたデータを生成
  const convertFilteredFinancialData = (): FinancialData[] => {
    if (!data?.recentTransactions || !selectedDescription) return [];
    
    const isMonthly = chartPeriod === 'monthly';
    const aggregatedData = new Map<string, FinancialData>();
    
    // トランザクションを期間ごとに集計
    data.recentTransactions.forEach(transaction => {
      // 選択された摘要のみを対象
      if (transaction.description !== selectedDescription) return;
      
      let key: string;
      let fiscalYear: number;
      
      if (isMonthly) {
        // 会計年度を計算
        fiscalYear = transaction.month >= (data.fiscalYearStartMonth || 4) 
          ? transaction.year 
          : transaction.year - 1;
          
        // 現在の会計年度のデータのみ
        if (fiscalYear !== currentFiscalYear) return;
        
        key = `${transaction.year}-${transaction.month}月`;
      } else {
        // 年度別のキー
        fiscalYear = transaction.month >= (data.fiscalYearStartMonth || 4) 
          ? transaction.year 
          : transaction.year - 1;
        key = `${fiscalYear}`;
      }
      
      const existing = aggregatedData.get(key) || {
        year: isMonthly ? transaction.year : fiscalYear,
        month: isMonthly ? transaction.month : undefined,
        generalIncome: 0,
        generalExpense: 0,
        reserveIncome: 0,
        reserveExpense: 0,
      };
      
      // 金額を追加
      if (transaction.account_type === 'general') {
        if (transaction.type === 'income') {
          existing.generalIncome += transaction.amount;
        } else {
          existing.generalExpense += transaction.amount;
        }
      } else {
        if (transaction.type === 'income') {
          existing.reserveIncome += transaction.amount;
        } else {
          existing.reserveExpense += transaction.amount;
        }
      }
      
      aggregatedData.set(key, existing);
    });
    
    // 配列に変換してソート
    return Array.from(aggregatedData.values()).sort((a, b) => {
      if (isMonthly) {
        // 月別の場合は月でソート
        const aMonth = a.month || 0;
        const bMonth = b.month || 0;
        return aMonth - bMonth;
      } else {
        // 年度別の場合は年でソート
        return a.year - b.year;
      }
    });
  };

  const getAccountType = (): 'general' | 'reserve' | 'both' => {
    if (showGeneral && showReserve) return 'both';
    if (showGeneral) return 'general';
    if (showReserve) return 'reserve';
    return 'both';
  };
  
  // 口座残高データの準備 - 最新年のデータから計算
  const calculateLatestBalance = (accountType: 'general' | 'reserve') => {
    if (!data?.yearlyData || data.yearlyData.length === 0) {
      // データがない場合はaccountBalancesから取得
      const balance = data?.accountBalances?.find(b => b.account_type === accountType);
      return balance?.balance || 0;
    }
    
    // 累積残高を計算
    let cumulativeBalance = 0;
    for (const yearData of data.yearlyData) {
      if (accountType === 'general') {
        cumulativeBalance += (yearData.generalIncome - yearData.generalExpense);
      } else {
        cumulativeBalance += (yearData.reserveIncome - yearData.reserveExpense);
      }
    }
    
    return cumulativeBalance;
  };
  
  // 摘要別支出データの集計
  const getExpensesByDescription = () => {
    if (!data?.recentTransactions) return [];
    
    const accountType = getAccountType();
    const expenseMap = new Map<string, number>();
    
    data.recentTransactions.forEach(transaction => {
      // 支出のみを対象
      if (transaction.type !== 'expense') return;
      
      // 会計種別でフィルタリング
      if (accountType === 'general' && transaction.account_type !== 'general') return;
      if (accountType === 'reserve' && transaction.account_type !== 'reserve') return;
      
      // 摘要ごとに集計
      const description = transaction.description || '未分類';
      const currentAmount = expenseMap.get(description) || 0;
      expenseMap.set(description, currentAmount + transaction.amount);
    });
    
    // 配列に変換してソート（金額の大きい順）
    return Array.from(expenseMap.entries())
      .map(([description, amount]) => ({ description, amount }))
      .sort((a, b) => b.amount - a.amount);
  };
  
  // 円グラフクリックハンドラ
  const handlePieChartClick = (description: string) => {
    // 同じ摘要をクリックした場合は選択解除
    setSelectedDescription(prev => prev === description ? null : description);
  };

  // チャートクリックハンドラ - 新しいAPI用
  const handleFinancialChartClick = (clickEvent: any) => {
    if (!clickEvent.accountType || !clickEvent.type) return;
    
    setSelectedChartData({
      period: clickEvent.label,
      accountType: clickEvent.accountType,
      type: clickEvent.type,
      amount: clickEvent.value
    });
  };

  // チャート期間変更
  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: 'monthly' | 'yearly' | null,
  ) => {
    if (newPeriod !== null) {
      setChartPeriod(newPeriod);
    }
  };


  // 明細データのフィルタリング
  const getFilteredDetailTransactions = (): Transaction[] => {
    if (!selectedChartData || !data?.recentTransactions) return [];
    
    // 選択された期間から年月を抽出
    let targetYear: number | null = null;
    let targetMonth: number | null = null;
    
    if (chartPeriod === 'monthly') {
      // "1月", "2月" などから月を抽出
      const monthMatch = selectedChartData.period.match(/(\d+)月/);
      if (monthMatch) {
        targetMonth = parseInt(monthMatch[1]);
        targetYear = currentFiscalYear;
        // 会計年度を考慮（例：4月開始で1-3月は次年度）
        if (data.fiscalYearStartMonth && targetMonth < data.fiscalYearStartMonth) {
          targetYear = currentFiscalYear + 1;
        }
      }
    } else {
      // 年別データの場合：「2022年度」または「2022」のパターンに対応
      const yearWithSuffixMatch = selectedChartData.period.match(/(\d+)年度/);
      const yearOnlyMatch = selectedChartData.period.match(/^(\d{4})$/);
      
      if (yearWithSuffixMatch) {
        targetYear = parseInt(yearWithSuffixMatch[1]);
      } else if (yearOnlyMatch) {
        targetYear = parseInt(yearOnlyMatch[1]);
      }
    }
    
    return data.recentTransactions.filter(originalTransaction => {
      // 基本条件：会計種別と収支種別が一致
      const baseMatch = originalTransaction.account_type === selectedChartData.accountType &&
                       originalTransaction.type === selectedChartData.type;
      
      if (!baseMatch) return false;
      
      // 期間条件
      if (chartPeriod === 'monthly' && targetYear && targetMonth) {
        // 月別表示：該当年月のデータのみ
        return originalTransaction.year === targetYear && originalTransaction.month === targetMonth;
      } else if (chartPeriod === 'yearly' && targetYear) {
        // 年別表示：該当年度のデータ（会計年度を考慮）
        if (data.fiscalYearStartMonth) {
          // 会計年度で判定
          const transactionFiscalYear = originalTransaction.month >= data.fiscalYearStartMonth 
            ? originalTransaction.year 
            : originalTransaction.year - 1;
          return transactionFiscalYear === targetYear;
        } else {
          // 暦年で判定
          return originalTransaction.year === targetYear;
        }
      }
      
      // 期間が特定できない場合はフィルタリングしない
      return false;
    }).map(t => ({
      id: t.id,
      transaction_date: t.transaction_date,
      amount: t.amount,
      type: t.type,
      account_type: t.account_type,
      category: t.category,
      description: t.description,
      reference_number: t.reference_number,
    }));
  };
  

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          データが見つかりません
        </Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <AppBar position="static" color="primary">
        <Toolbar>
          <Public sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h1">
              {data.organization.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              収支ダッシュボード
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* 口座残高カード */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          {/* 一般会計残高 */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" component="h2">
                  一般会計
                </Typography>
                <Typography 
                  variant="h4" 
                  color={calculateLatestBalance('general') >= 0 ? 'primary' : 'error'} 
                  sx={{ mt: 2 }}
                  fontWeight="bold"
                >
                  {formatCurrency(calculateLatestBalance('general'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  現在残高
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          {/* 積立会計残高 */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" component="h2">
                  積立会計
                </Typography>
                <Typography 
                  variant="h4" 
                  color={calculateLatestBalance('reserve') >= 0 ? 'primary' : 'error'} 
                  sx={{ mt: 2 }}
                  fontWeight="bold"
                >
                  {formatCurrency(calculateLatestBalance('reserve'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  現在残高
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* 収支グラフ */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6">
                収支推移
              </Typography>
              {/* 月単位表示時の会計年度ナビゲーション */}
              {chartPeriod === 'monthly' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <IconButton 
                    onClick={handlePreviousYear}
                    size="small"
                  >
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="body1" sx={{ minWidth: '120px', textAlign: 'center', fontWeight: 'bold' }}>
                    {currentFiscalYear}年度
                  </Typography>
                  <IconButton 
                    onClick={handleNextYear}
                    size="small"
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* 表示切り替えチェックボックス */}
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showGeneral}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowGeneral(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="一般会計"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showReserve}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowReserve(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="積立会計"
                />
              </FormGroup>

              {/* 期間切り替え */}
              <ToggleButtonGroup
                value={chartPeriod}
                exclusive
                onChange={handlePeriodChange}
                size="small"
              >
                <ToggleButton value="monthly">
                  月単位
                </ToggleButton>
                <ToggleButton value="yearly">
                  年単位
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* グラフの説明 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              📊 収入（正の棒）・支出（負の棒）・収支（線グラフ）で財務状況を可視化
              {showGeneral && showReserve && ' • 両方選択時は積み上げ表示'}
              {chartPeriod === 'monthly' && currentFiscalYear && data.fiscalYearStartMonth && ` • ${currentFiscalYear}年度（${data.fiscalYearStartMonth}月〜${data.fiscalYearStartMonth === 1 ? 12 : data.fiscalYearStartMonth - 1}月）`}
              • 棒グラフをクリックすると該当する取引明細が表示されます
            </Typography>
          </Box>
          
          {(showGeneral || showReserve) ? (
            <FinancialChart
              data={convertToFinancialData()}
              period={chartPeriod}
              accountType={getAccountType()}
              showBalanceLine={true}
              showCumulativeBalance={true}
              onChartClick={handleFinancialChartClick}
              height={450}
              currentFiscalYear={currentFiscalYear}
            />
          ) : (
            <Box sx={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                表示する会計を選択してください
              </Typography>
            </Box>
          )}
        </Paper>

        {/* 明細表示と円グラフ */}
        {selectedChartData && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* 明細テーブル */}
            <Box sx={{ flex: '1 1 600px', minWidth: '400px' }}>
              <TransactionDetailTable
                transactions={getFilteredDetailTransactions()}
                title={selectedChartData.period + '年度'}
                subtitle={`${selectedChartData.accountType === 'general' ? '一般会計' : '積立会計'} ${selectedChartData.type === 'income' ? '収入' : '支出'}明細`}
                totalAmount={selectedChartData.amount}
              />
            </Box>
            
            {/* 摘要別支出円グラフ */}
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <ExpensePieChart
                data={getExpensesByDescription()}
                accountType={getAccountType()}
                onDescriptionClick={handlePieChartClick}
              />
              
              {/* 選択中の摘要表示 */}
              {selectedDescription && (
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="body2" color="primary">
                    選択中の摘要: {selectedDescription}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    棒グラフには「{selectedDescription}」の支出のみが表示されています
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        )}

        {/* フッター */}
        <Box sx={{ mt: 4, py: 2, textAlign: 'center' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            マンション管理組合 家計簿システム
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ※ このページは読み取り専用です
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicDashboard;